import { animated, useSpring } from '@react-spring/web';
import { CSSProperties, useMemo } from 'react';

import styles from './discoBall.module.scss';

interface DiscoBallProps {
  size?: number;
  position: 'left' | 'right' | 'center';
  isRevealed: boolean;
  delay?: number;
}

interface Tile {
  x: number;
  y: number;
  z: number;
  rotateI: number;
  rotateT: number;
  color: string;
  animationDelay: number;
}

// Mostly silver/mirror tiles with occasional neon colors
const TILE_COLORS = [
  // Silver/mirror tones (high frequency) - sharper, more contrast
  '#ffffff', // pure white
  '#f0f0f0', // bright white
  '#e8e8e8', // bright silver
  '#d8d8d8', // light silver
  '#c8c8c8', // silver
  '#b8b8b8', // medium silver
  '#a0a0a0', // darker silver
  '#888888', // dark grey for contrast
  '#d8d8e8', // silver with cool tint
  '#e8e8f0', // bright cool silver
];

// Neon color specks (low frequency ~12%) - more saturated
const SPECK_COLORS = [
  '#ff00ff', // magenta
  '#00ffff', // cyan
  '#ff00dd', // hot pink
  '#00ffdd', // teal
  '#ff44ff', // bright magenta
  '#44ffff', // bright cyan
  '#dd00ff', // purple
  '#ff00aa', // pink-magenta
];

// Generate tiles using the CodePen algorithm
function generateDiscoBallTiles(radius: number, squareSize: number): Tile[] {
  const tiles: Tile[] = [];
  const prec = 19.55;
  const fuzzy = 0.001;
  const inc = (Math.PI - fuzzy) / prec;

  for (let t = fuzzy; t < Math.PI; t += inc) {
    const z = radius * Math.cos(t);
    const currentRadius =
      Math.abs(
        radius * Math.cos(0) * Math.sin(t) -
          radius * Math.cos(Math.PI) * Math.sin(t),
      ) / 2.5;
    const circumference = Math.abs(2 * Math.PI * currentRadius);
    const squaresThatFit = Math.floor(circumference / squareSize);
    const angleInc = (Math.PI * 2 - fuzzy) / squaresThatFit;

    for (let i = angleInc / 2 + fuzzy; i < Math.PI * 2; i += angleInc) {
      const x = radius * Math.cos(i) * Math.sin(t);
      const y = radius * Math.sin(i) * Math.sin(t);

      // Mostly silver, with ~15% chance of a neon color speck
      const isColorSpeck = Math.random() < 0.15;
      const color = isColorSpeck
        ? SPECK_COLORS[Math.floor(Math.random() * SPECK_COLORS.length)]
        : TILE_COLORS[Math.floor(Math.random() * TILE_COLORS.length)];
      const animationDelay = Math.floor(Math.random() * 20) / 10;

      tiles.push({
        x,
        y,
        z,
        rotateI: i,
        rotateT: t,
        color,
        animationDelay,
      });
    }
  }

  return tiles;
}

export function DiscoBall({
  size = 140,
  position,
  isRevealed,
  delay = 0,
}: DiscoBallProps): JSX.Element {
  const radius = size / 2;
  const squareSize = size / 20; // Scale square size with ball size

  // Generate tiles once
  const tiles = useMemo(
    () => generateDiscoBallTiles(radius, squareSize),
    [radius, squareSize],
  );

  // Spring for position and scale when revealing
  const positionSpring = useSpring({
    transform: isRevealed
      ? position === 'left'
        ? `translateX(-350px) scale(0.6)`
        : position === 'right'
          ? `translateX(350px) scale(0.6)`
          : `translateX(0) scale(1)`
      : `translateX(0) scale(1)`,
    opacity: isRevealed && position !== 'center' ? 0.7 : 1,
    config: { tension: 80, friction: 20 },
    delay: isRevealed ? delay : 0,
  });

  return (
    <animated.div
      className={styles.discoBallWrapper}
      style={{
        ...positionSpring,
        width: size,
        height: size,
      }}
    >
      {/* Hanging string */}
      <div className={styles.string} />

      {/* Light rays emanating from the ball */}
      <div className={styles.lightRaysContainer}>
        {[...Array(12)].map((_, i) => {
          // Vibrant colors matching the gradient phases
          const rayColors = [
            '#ff00ff', // magenta - vibrant
            '#6a1a6a', // deep magenta
            '#ff00cc', // hot pink - vibrant
            '#501468', // purple
            '#00ffcc', // cyan - vibrant
            '#7a1c75', // purple-magenta
            '#ff44aa', // pink - vibrant
            '#3c1260', // medium purple
            '#00ddff', // bright cyan - vibrant
            '#281058', // dark purple
            '#ff66cc', // light pink - vibrant
            '#1e1055', // deep purple
          ];
          return (
            <div
              key={i}
              className={styles.lightRay}
              style={
                {
                  '--ray-angle': `${i * 30}deg`,
                  '--ray-color': rayColors[i % rayColors.length],
                  '--ray-delay': `${i * 0.15}s`,
                } as CSSProperties
              }
            />
          );
        })}
      </div>

      {/* The 3D disco ball */}
      <div
        className={styles.discoBall}
        style={
          {
            width: size,
            height: size,
            '--ball-size': `${size}px`,
          } as CSSProperties
        }
      >
        {/* Inner dark sphere */}
        <div className={styles.discoBallMiddle} />

        {/* Mirror tiles */}
        {tiles.map((tile, index) => (
          <div
            key={index}
            className={styles.square}
            style={
              {
                '--x': `${tile.x}px`,
                '--y': `${tile.y}px`,
                '--z': `${tile.z}px`,
              } as CSSProperties
            }
          >
            <div
              className={styles.squareTile}
              style={
                {
                  '--rotate-i': `${tile.rotateI}rad`,
                  '--rotate-t': `${tile.rotateT}rad`,
                  '--tile-color': tile.color,
                  '--animation-delay': `${tile.animationDelay}s`,
                  width: `${squareSize}px`,
                  height: `${squareSize}px`,
                } as CSSProperties
              }
            />
          </div>
        ))}
      </div>

      {/* Glow effect under the ball */}
      <div className={styles.glow} />
    </animated.div>
  );
}
